/**
 * Ingredients Slider
 * Consolidated file for ingredients slider functionality including flavor handling
 */

// Events needed for variant change detection
const INGREDIENTS_SLIDER_EVENTS = {
  PRODUCT_VARIANT_CHANGING: "product:variant:changing",
  PRODUCT_VARIANT_CHANGED: "product:variant:changed"
};

/**
 * Ingredients Slider Flavor Handler
 * Manages flavor-specific slider content and updates slider when variant changes
 */
class IngredientsSliderFlavorHandler {
  constructor() {
    this.sliderContainer = null;
    this.flavorContentMap = new Map();
    this.currentFlavor = null;
    this.pendingFlavor = null;
    this.isUpdating = false; // Flag to prevent re-detection during update
    this.init();
  }

  init() {
    // Find the ingredients slider container
    this.sliderContainer = document.querySelector('[data-ingredients-slider]');
    
    if (!this.sliderContainer) {
      console.warn('⚠️ Ingredients slider container not found');
      return;
    }

    // Load default content from the slider
    this.loadDefaultContent();

    // Get initial flavor
    this.currentFlavor = this.getCurrentFlavor();
    console.log('🎯 Initial flavor detected:', this.currentFlavor);

    // Listen for variant changes on the product section (more reliable)
    const productSection = document.querySelector('[js-product-section], main-product-default, featured-product');
    
    if (productSection) {
      // Listen to variant changing event (fires first, has newOptionInput)
      productSection.addEventListener(
        INGREDIENTS_SLIDER_EVENTS.PRODUCT_VARIANT_CHANGING,
        this.handleVariantChanging.bind(this)
      );
      
      // Also listen to variant changed as fallback
      productSection.addEventListener(
        INGREDIENTS_SLIDER_EVENTS.PRODUCT_VARIANT_CHANGED,
        this.handleVariantChange.bind(this)
      );
      
      console.log('👂 Listening for variant changes on product section');
    }

    // Also listen on document as fallback
    document.addEventListener(
      INGREDIENTS_SLIDER_EVENTS.PRODUCT_VARIANT_CHANGING,
      this.handleVariantChanging.bind(this)
    );

    document.addEventListener(
      INGREDIENTS_SLIDER_EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleVariantChange.bind(this)
    );

    console.log('✅ Ingredients slider flavor handler initialized');
  }

  /**
   * Load default slider content from the DOM
   * Only loads if 'default' flavor is not already registered
   */
  loadDefaultContent() {
    // Don't overwrite if default is already registered
    if (this.flavorContentMap.has('default')) {
      return;
    }
    
    const sliderComponent = this.sliderContainer?.querySelector('scroll-slider-with-scroll-indicator');
    const sliderElement = sliderComponent?.querySelector('.scroll-slider__slider');
    
    if (sliderElement) {
      const defaultHTML = sliderElement.innerHTML;
      const itemCount = sliderElement.children.length;
      
      // Only register if we have content
      if (defaultHTML && itemCount > 0) {
        this.flavorContentMap.set('default', {
          html: defaultHTML,
          count: itemCount
        });

        console.log('📦 Loaded default slider content:', itemCount, 'items');
      }
    }
  }

  /**
   * Get the current selected flavor from variant
   */
  getCurrentFlavor() {
    console.log('🔍 Detecting current flavor...');
    
    // Method 1: Try to get from option 1 inputs specifically (flavor option)
    // Pattern: input with ID containing "-1-" (e.g., "template--19265839071415__product_overview-1-2")
    const option1Inputs = document.querySelectorAll(
      'input[id*="-1-"]:checked, ' +
      'input[name*="option"][name*="1"]:checked, ' +
      'input[data-option="1"]:checked'
    );
    console.log('Option 1 inputs found:', option1Inputs.length);
    
    if (option1Inputs.length > 0) {
      const option1Input = option1Inputs[0];
      let flavorValue = option1Input.value || 
                       option1Input.getAttribute('value') || 
                       option1Input.dataset.value ||
                       '';
      
      // Try to get from label
      if (!flavorValue || flavorValue.match(/^\d+$/)) {
        const label = option1Input.closest('label');
        if (label) {
          flavorValue = label.textContent?.trim() || label.innerText?.trim() || '';
        }
      }
      
      // Try to get from chip/swatch
      if (!flavorValue || flavorValue.match(/^\d+$/)) {
        const chip = option1Input.closest('.chip, .swatch, [data-option-value], .variant-option');
        if (chip) {
          flavorValue = chip.dataset.optionValue || 
                       chip.getAttribute('data-value') ||
                       chip.textContent?.trim() ||
                       chip.innerText?.trim() ||
                       '';
        }
      }
      
      if (flavorValue && !flavorValue.match(/^\d+$/)) {
        console.log('Flavor from option 1 input:', flavorValue);
        return this.normalizeFlavorName(flavorValue);
      }
    }
    
    // Method 2: Fallback to any option input (first one is usually flavor)
    const optionInputs = document.querySelectorAll('input[name*="option"]:checked');
    console.log('All option inputs found:', optionInputs.length);
    
    if (optionInputs.length > 0) {
      // Usually first option is flavor (Option1 = Flavor)
      const firstInput = optionInputs[0];
      let flavorValue = firstInput.value;
      
      // Try to get from label
      if (!flavorValue || flavorValue.match(/^\d+$/)) {
        const label = firstInput.closest('label');
        if (label) {
          flavorValue = label.textContent?.trim() || label.innerText?.trim() || '';
        }
      }
      
      if (flavorValue && !flavorValue.match(/^\d+$/)) {
        console.log('Flavor from first option input:', flavorValue);
        return this.normalizeFlavorName(flavorValue);
      }
    }

    // Method 3: Try to get from variant selector
    const variantInput = document.querySelector('input[name="id"]:checked, select[name="id"]');
    
    if (variantInput) {
      const variantId = variantInput.value;
      console.log('Variant ID:', variantId);
      
      // Try to get variant title from select dropdown
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect && variantSelect.selectedIndex >= 0) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantTitle = selectedOption?.text || selectedOption?.label || '';
        console.log('Variant title:', variantTitle);
        
        // Extract flavor from variant title (e.g., "Vanilla - 500g" -> "Vanilla")
        const flavorMatch = variantTitle.match(/^([^-]+)/);
        if (flavorMatch) {
          const flavor = this.normalizeFlavorName(flavorMatch[1]);
          console.log('Extracted flavor:', flavor);
          return flavor;
        }
      }

      // Try to get from data attributes or variant object
      const variantElement = variantInput.closest('[data-variant-id]') || 
                             document.querySelector(`[data-variant-id="${variantId}"]`);
      if (variantElement) {
        const variantTitle = variantElement.dataset.variantTitle || 
                            variantElement.getAttribute('data-title') || '';
        if (variantTitle) {
          const flavorMatch = variantTitle.match(/^([^-]+)/);
          if (flavorMatch) {
            return this.normalizeFlavorName(flavorMatch[1]);
          }
        }
      }
    }

    console.log('⚠️ No flavor detected, using default');
    return 'default';
  }

  /**
   * Get flavor from variant change event
   * @param {Event} event - The variant change event
   */
  getFlavorFromEvent(event) {
    if (!event || !event.detail) {
      return null;
    }

    // Use newOptionInput from event detail (most reliable)
    const newOptionInput = event.detail.newOptionInput;
    
    if (newOptionInput) {
      const inputName = newOptionInput.name || '';
      const inputId = newOptionInput.id || '';
      const inputValue = newOptionInput.value || '';
      const inputType = newOptionInput.type || '';
      
      console.log('🔍 Checking input:', { 
        name: inputName, 
        id: inputId, 
        value: inputValue,
        type: inputType,
        element: newOptionInput
      });
      
      // Check if this is the first option (flavor option)
      // Pattern examples:
      // - ID: "template--19265839071415__product_overview-1-0" (option 1, value 0)
      // - Name: "options[Flavour]" or contains "option" and "1"
      const isOption1 = 
        inputId.match(/-1-/) || // ID pattern: -1- means option 1
        (inputName.includes('option') && (inputName.includes('1') || inputName.toLowerCase().includes('flavour') || inputName.toLowerCase().includes('flavor')));
      
      if (isOption1) {
        // Get value from input
        let flavorValue = newOptionInput.value || 
                         newOptionInput.getAttribute('value') || 
                         newOptionInput.dataset.value ||
                         '';
        
        console.log('Input value:', flavorValue);
        
        // If value is empty or looks like an ID, try to get from label or text content
        if (!flavorValue || flavorValue.match(/^\d+$/)) {
          const label = newOptionInput.closest('label');
          if (label) {
            const labelText = label.textContent?.trim() || label.innerText?.trim() || '';
            if (labelText) {
              flavorValue = labelText;
              console.log('Using label text:', flavorValue);
            }
          }
        }
        
        // If still empty, try to get from parent chip/swatch element
        if (!flavorValue || flavorValue.match(/^\d+$/)) {
          const chip = newOptionInput.closest('.chip, .swatch, [data-option-value], .variant-option');
          if (chip) {
            flavorValue = chip.dataset.optionValue || 
                         chip.getAttribute('data-value') ||
                         chip.textContent?.trim() ||
                         chip.innerText?.trim() ||
                         '';
            if (flavorValue) {
              console.log('Using chip/swatch value:', flavorValue);
            }
          }
        }
        
        // Try to get from data attributes on the input itself
        if (!flavorValue || flavorValue.match(/^\d+$/)) {
          flavorValue = newOptionInput.dataset.label ||
                        newOptionInput.getAttribute('data-label') ||
                        newOptionInput.title ||
                        '';
          if (flavorValue) {
            console.log('Using data attribute:', flavorValue);
          }
        }
        
      if (flavorValue && !flavorValue.match(/^\d+$/)) {
        console.log('🎯 Flavor from event input:', flavorValue);
        return this.normalizeFlavorName(flavorValue);
      } else {
        console.log('⚠️ Could not extract flavor value from input');
      }
      } else {
        console.log('⏭️ Input is not option 1 (flavor), trying to find option 1 input...');
        
        // Fallback: Find the checked option 1 input
        const option1Inputs = document.querySelectorAll('input[name*="option"][name*="1"]:checked, input[id*="-1-"]:checked');
        if (option1Inputs.length > 0) {
          const option1Input = option1Inputs[0];
          let flavorValue = option1Input.value || 
                           option1Input.getAttribute('value') || 
                           option1Input.dataset.value ||
                           '';
          
          // Try to get from label
          if (!flavorValue || flavorValue.match(/^\d+$/)) {
            const label = option1Input.closest('label');
            if (label) {
              flavorValue = label.textContent?.trim() || label.innerText?.trim() || '';
            }
          }
          
          // Try to get from chip/swatch
          if (!flavorValue || flavorValue.match(/^\d+$/)) {
            const chip = option1Input.closest('.chip, .swatch, [data-option-value], .variant-option');
            if (chip) {
              flavorValue = chip.dataset.optionValue || 
                           chip.getAttribute('data-value') ||
                           chip.textContent?.trim() ||
                           chip.innerText?.trim() ||
                           '';
            }
          }
          
          if (flavorValue && !flavorValue.match(/^\d+$/)) {
            console.log('🎯 Flavor from option 1 input:', flavorValue);
            return this.normalizeFlavorName(flavorValue);
          }
        }
      }
    }

    return null;
  }

  /**
   * Handle variant changing event
   */
  handleVariantChanging(event) {
    console.log('🔄 Variant changing event received', event);
    console.log('Event detail:', event.detail);
    
    // Try to get flavor from the event
    const flavorFromEvent = this.getFlavorFromEvent(event);
    
    if (flavorFromEvent && flavorFromEvent !== this.currentFlavor) {
      console.log('🔄 Flavor changing:', this.currentFlavor, '→', flavorFromEvent);
      this.currentFlavor = flavorFromEvent;
      this.pendingFlavor = flavorFromEvent; // Store for changed event
      
      // Update slider immediately
      this.updateSliderForFlavor(flavorFromEvent);
    }
  }

  /**
   * Handle variant changed event
   */
  handleVariantChange(event) {
    console.log('✅ Variant changed event received', event);
    console.log('Event detail:', event.detail);
    
    // If we already updated from changing event, don't override
    if (this.pendingFlavor && this.pendingFlavor === this.currentFlavor) {
      console.log('⏭️ Already updated from changing event, skipping');
      this.pendingFlavor = null; // Clear pending
      return;
    }
    
    // Try to get flavor from the event first
    const flavorFromEvent = this.getFlavorFromEvent(event);
    
    if (flavorFromEvent) {
      if (flavorFromEvent !== this.currentFlavor) {
        console.log('🔄 Flavor changed:', this.currentFlavor, '→', flavorFromEvent);
        this.currentFlavor = flavorFromEvent;
        this.updateSliderForFlavor(flavorFromEvent);
      } else {
        console.log('⏭️ Flavor unchanged, skipping update');
      }
      return;
    }
    
    // Fallback: Wait a bit for DOM to update with new variant selection
    setTimeout(() => {
      // Don't re-detect if we're currently updating the slider
      if (this.isUpdating) {
        console.log('⏭️ Slider update in progress, skipping re-detection');
        return;
      }
      
      // Try to get from the input that triggered the change
      const variantInput = document.querySelector('input[name="id"]:checked, select[name="id"]');
      if (variantInput) {
        // Find the option 1 input that's checked (flavor)
        const option1Input = document.querySelector('input[id*="-1-"]:checked');
        if (option1Input) {
          let flavorValue = '';
          const label = option1Input.closest('label');
          if (label) {
            flavorValue = label.textContent?.trim() || label.innerText?.trim() || '';
          }
          
          if (!flavorValue) {
            const chip = option1Input.closest('.chip, .swatch, [data-option-value], .variant-option');
            if (chip) {
              flavorValue = chip.dataset.optionValue || 
                           chip.getAttribute('data-value') ||
                           chip.textContent?.trim() ||
                           chip.innerText?.trim() ||
                           '';
            }
          }
          
          if (flavorValue && !flavorValue.match(/^\d+$/)) {
            const newFlavor = this.normalizeFlavorName(flavorValue);
            if (newFlavor !== this.currentFlavor) {
              console.log('🔄 Flavor changed (from fallback):', this.currentFlavor, '→', newFlavor);
              this.currentFlavor = newFlavor;
              this.updateSliderForFlavor(newFlavor);
              return;
            }
          }
        }
      }
      
      const newFlavor = this.getCurrentFlavor();
      console.log('Current flavor:', this.currentFlavor, '→ New flavor:', newFlavor);
      
      if (newFlavor === this.currentFlavor) {
        console.log('⏭️ Flavor unchanged, skipping update');
        return; // No change needed
      }

      // Only update if flavor actually changed, we're not currently updating, and new flavor is not 'default' (unless current is also default)
      if (!this.isUpdating) {
        // Don't reset to default if we have a valid flavor and detection failed
        if (newFlavor === 'default' && this.currentFlavor !== 'default' && this.currentFlavor !== null) {
          console.log('⚠️ Flavor detection returned default, but current flavor is', this.currentFlavor, '- keeping current flavor');
          return; // Keep current flavor, don't reset to default
        }
        
        console.log('🔄 Flavor changed:', this.currentFlavor, '→', newFlavor);
        this.currentFlavor = newFlavor;
        this.updateSliderForFlavor(newFlavor);
      }
    }, 200);
  }

  /**
   * Normalize flavor name to match Liquid handleize format
   * Converts "Vanilla Cinnamon" -> "vanilla-cinnamon"
   */
  normalizeFlavorName(flavor) {
    return flavor.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  updateSliderForFlavor(flavor) {
    const normalizedFlavor = this.normalizeFlavorName(flavor);
    const content = this.flavorContentMap.get(normalizedFlavor);
    
    if (!content) {
      console.warn(`⚠️ No content found for flavor: "${normalizedFlavor}"`);
      console.warn(`📋 Registered flavors:`, Array.from(this.flavorContentMap.keys()));
      
      // Retry after a short delay in case flavors are still being registered
      if (!this.retryTimeouts) {
        this.retryTimeouts = new Map();
      }
      
      const retryKey = normalizedFlavor;
      if (!this.retryTimeouts.has(retryKey)) {
        let retryCount = 0;
        const maxRetries = 5;
        
        const retryCheck = () => {
          retryCount++;
          const retryContent = this.flavorContentMap.get(normalizedFlavor);
          
          if (retryContent) {
            console.log(`✅ Flavor "${normalizedFlavor}" found after ${retryCount} retry(ies)`);
            this.retryTimeouts.delete(retryKey);
            this.updateSlider(retryContent.html, retryContent.count);
            return;
          }
          
          if (retryCount < maxRetries) {
            this.retryTimeouts.set(retryKey, setTimeout(retryCheck, 200));
          } else {
            console.warn(`⏸️ Flavor "${normalizedFlavor}" not found after ${maxRetries} retries`);
            this.retryTimeouts.delete(retryKey);
            
            // Fallback to default if available
            const defaultContent = this.flavorContentMap.get('default');
            if (defaultContent) {
              this.updateSlider(defaultContent.html, defaultContent.count);
            }
          }
        };
        
        this.retryTimeouts.set(retryKey, setTimeout(retryCheck, 200));
      }
      
      return;
    }

    this.updateSlider(content.html, content.count);
  }

  /**
   * Update the slider with new content
   */
  updateSlider(html, itemCount) {
    console.log('📤 Updating slider with', itemCount, 'items');
    
    if (!window.rebuildIngredientsSlider) {
      console.error('❌ rebuildIngredientsSlider function not found');
      console.log('Available functions:', Object.keys(window).filter(k => k.includes('rebuild') || k.includes('slider')));
      return;
    }

    if (!this.sliderContainer) {
      console.error('❌ Slider container not found');
      return;
    }

    // Set flag to prevent re-detection during update
    this.isUpdating = true;
    
    console.log('✅ Calling rebuildIngredientsSlider...');
    window.rebuildIngredientsSlider(html, itemCount, this.sliderContainer);
    
    // Clear flag after a delay to allow rebuild to complete
    setTimeout(() => {
      this.isUpdating = false;
    }, 500);
  }

  /**
   * Register flavor-specific slider content
   * @param {String} flavor - The flavor identifier (e.g., 'vanilla', 'chocolate')
   * @param {String} html - The HTML content for slider items
   * @param {Number} itemCount - The number of items
   */
  registerFlavorContent(flavor, html, itemCount) {
    const normalizedFlavor = this.normalizeFlavorName(flavor);
    this.flavorContentMap.set(normalizedFlavor, {
      html: html,
      count: itemCount
    });
    console.log(`📝 Registered content for flavor: ${normalizedFlavor} (${itemCount} items)`);
    
    // If this is the current flavor and we were waiting for it, update now
    if (this.currentFlavor === normalizedFlavor && this.retryTimeouts && this.retryTimeouts.has(normalizedFlavor)) {
      clearTimeout(this.retryTimeouts.get(normalizedFlavor));
      this.retryTimeouts.delete(normalizedFlavor);
      this.updateSlider(html, itemCount);
    }
  }

  /**
   * Manually trigger slider update for a specific flavor
   * @param {String} flavor - The flavor identifier
   */
  switchToFlavor(flavor) {
    const normalizedFlavor = this.normalizeFlavorName(flavor);
    this.currentFlavor = normalizedFlavor;
    this.updateSliderForFlavor(normalizedFlavor);
  }
}

// Initialize on page load
let ingredientsSliderFlavorHandler = null;

let initRetryCount = 0;
const MAX_RETRIES = 10;

function initializeFlavorHandler() {
  // Check if slider container exists
  const sliderContainer = document.querySelector('[data-ingredients-slider]');
  
  if (!sliderContainer) {
    initRetryCount++;
    if (initRetryCount < MAX_RETRIES) {
      console.warn(`⚠️ Ingredients slider container not found, retrying... (${initRetryCount}/${MAX_RETRIES})`);
      // Retry after a short delay (slider might load dynamically)
      setTimeout(initializeFlavorHandler, 500);
    } else {
      console.error('❌ Ingredients slider container not found after max retries');
    }
    return;
  }

  if (ingredientsSliderFlavorHandler) {
    console.log('⚠️ Flavor handler already initialized');
    return;
  }

  ingredientsSliderFlavorHandler = new IngredientsSliderFlavorHandler();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFlavorHandler);
} else {
  // If DOM is already loaded, try immediately, then retry if needed
  initializeFlavorHandler();
}

/**
 * Global function to register flavor-specific slider content
 * @param {String} flavor - The flavor identifier
 * @param {String} html - The HTML content for slider items
 * @param {Number} itemCount - The number of items
 */
window.registerIngredientsSliderFlavor = function(flavor, html, itemCount) {
  if (ingredientsSliderFlavorHandler) {
    ingredientsSliderFlavorHandler.registerFlavorContent(flavor, html, itemCount);
  } else {
    console.warn('⚠️ Ingredients slider flavor handler not initialized yet');
    // Store for later initialization
    if (!window._pendingFlavorContent) {
      window._pendingFlavorContent = [];
    }
    window._pendingFlavorContent.push({ flavor, html, itemCount });
  }
};

/**
 * Global function to manually switch to a specific flavor
 * @param {String} flavor - The flavor identifier
 */
window.switchIngredientsSliderFlavor = function(flavor) {
  if (ingredientsSliderFlavorHandler) {
    ingredientsSliderFlavorHandler.switchToFlavor(flavor);
  } else {
    console.warn('⚠️ Ingredients slider flavor handler not initialized yet');
  }
};

/**
 * Global function to manually trigger variant change detection and update
 * Useful for testing or if events aren't firing
 */
window.updateIngredientsSliderForCurrentVariant = function() {
  if (ingredientsSliderFlavorHandler) {
    const currentFlavor = ingredientsSliderFlavorHandler.getCurrentFlavor();
    console.log('🔄 Manually updating slider for flavor:', currentFlavor);
    ingredientsSliderFlavorHandler.updateSliderForFlavor(currentFlavor);
  } else {
    console.warn('⚠️ Ingredients slider flavor handler not initialized yet');
  }
};

// Apply pending registrations after initialization
if (window._pendingFlavorContent) {
  const checkInit = setInterval(() => {
    if (ingredientsSliderFlavorHandler) {
      window._pendingFlavorContent.forEach(({ flavor, html, itemCount }) => {
        ingredientsSliderFlavorHandler.registerFlavorContent(flavor, html, itemCount);
      });
      window._pendingFlavorContent = [];
      clearInterval(checkInit);
    }
  }, 100);
}

